// src/services/volume.js — how loud Vibes is, remembered.
//
// ══ ONE LEVEL, TWO SURFACES ════════════════════════════════════════════════
// There is a single volume LEVEL shared by the whole app — turn it down on a
// feed video and the reel you open next is already at that level, because
// "quiet" is a statement about the room you are in, not about one video.
//
// 🟥 BUT MUTE IS PER SURFACE, and that is deliberate:
//
//    · THE FEED starts MUTED, like every feed ever built. Videos scroll past
//      you without being asked for; a browser will not even autoplay them with
//      sound, so a feed that started unmuted would just be a feed that silently
//      refuses to play.
//    · REELS start UNMUTED. You got there by clicking, which is both a
//      statement of intent and the user gesture the browser needs, and a reel
//      with its soundtrack muted is a reel with the point removed.
//
//    Sharing one mute flag between them would mean either a feed that shouts at
//    you or reels that are silent — so each surface remembers its own answer,
//    and both move the same slider.
//
// 🟥 THE LEVEL IS A SCALE, NOT A VOLUME. It multiplies whatever the author
//    chose (`originalVolume` / `addedSoundVolume` on the post), so a post mixed
//    quiet stays quieter than a post mixed loud. Overriding those outright
//    would flatten every mix to the same loudness and undo the author's work.
//
// Persisted, because a volume you set is a preference and asking for it twice
// is asking for it once too often.

const KEY = 'allo:volume';

const DEFAULTS = { level: 0.85, feed: true, reels: false };  // feed/reels = muted?

function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (!raw || typeof raw !== 'object') return { ...DEFAULTS };
    const lvl = Number(raw.level);
    return {
      level: Number.isFinite(lvl) ? Math.max(0, Math.min(1, lvl)) : DEFAULTS.level,
      feed: typeof raw.feed === 'boolean' ? raw.feed : DEFAULTS.feed,
      reels: typeof raw.reels === 'boolean' ? raw.reels : DEFAULTS.reels,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

let state = load();
const listeners = new Set();

function emit() {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
  listeners.forEach((fn) => { try { fn(state); } catch {} });
}

export function subscribeVolume(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function volumeState() { return state; }

/** The scale a surface should apply right now: 0 when muted. */
export function scaleFor(scope) {
  return state[scope] ? 0 : state.level;
}

export function setLevel(next) {
  const level = Math.max(0, Math.min(1, Number(next) || 0));
  // 🟢 DRAGGING THE SLIDER OFF ZERO UNMUTES. Nobody drags a slider up in order
  //    to hear nothing, and leaving the mute on would make the control look
  //    broken. Dragging it to zero is the same gesture as muting.
  state = { ...state, level };
  emit();
}

export function setLevelFor(scope, next) {
  const level = Math.max(0, Math.min(1, Number(next) || 0));
  state = { ...state, level, [scope]: level === 0 };
  emit();
}

export function setMuted(scope, muted) {
  const next = { ...state, [scope]: !!muted };
  // un-muting something that sits at zero has to give it a level to come back to
  if (!muted && next.level <= 0.001) next.level = DEFAULTS.level;
  state = next;
  emit();
}

export function toggleMuted(scope) { setMuted(scope, !state[scope]); }
